import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import './LoginPage.css';
import ZaggerLogo from '../components/ZaggerLogo';

function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  const goToLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  // Check for password reset: PKCE (?code=...) or implicit (#access_token=...&type=recovery)
  useEffect(() => {
    const search = window.location.search;
    const searchParams = new URLSearchParams(search);
    const code = searchParams.get('code');
    let hash = window.location.hash;

    // 1) PKCE flow: Supabase redirects with ?code=... instead of hash (default in newer Supabase)
    if (code) {
      console.log('🔍 PKCE recovery code present, exchanging for session...');
      supabase.auth.exchangeCodeForSession(code)
        .then(({ data, error }) => {
          if (error) {
            console.error('Error exchanging code for session:', error);
            setError('Invalid or expired password reset link. Please request a new one.');
            setTimeout(goToLogin, 3000);
            return;
          }
          if (data.session) {
            console.log('✅ Session established from PKCE code');
            window.history.replaceState(null, '', window.location.pathname + (window.location.hash || ''));
            setMessage('Please enter your new password below.');
          } else {
            setError('Invalid or expired password reset link. Please request a new one.');
            setTimeout(goToLogin, 3000);
          }
        });
      return;
    }

    // 2) Implicit flow: token in hash, or in query (if hash was lost) — move query to hash once
    if ((!hash || hash.length < 2) && search) {
      const accessToken = searchParams.get('access_token');
      const type = searchParams.get('type');
      const refreshToken = searchParams.get('refresh_token');
      if (type === 'recovery' && accessToken) {
        let newHash = `#access_token=${encodeURIComponent(accessToken)}&type=recovery`;
        if (refreshToken) {
          newHash += `&refresh_token=${encodeURIComponent(refreshToken)}`;
        }
        window.history.replaceState(null, '', window.location.pathname + newHash);
        hash = newHash;
      }
    }

    if (!hash || hash.length < 2) {
      console.warn('⚠️  No recovery code or token in URL');
      setError('Invalid or expired password reset link. Please request a new one.');
      setTimeout(goToLogin, 3000);
      return;
    }

    const hashParams = new URLSearchParams(hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    if (type === 'recovery' && accessToken) {
      console.log('✅ Valid reset token in hash, establishing recovery session...');
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || '',
      }).then(({ data, error: sessionError }) => {
        if (sessionError || !data.session) {
          console.error('Error establishing recovery session:', sessionError);
          setError('Invalid or expired password reset link. Please request a new one.');
          setTimeout(goToLogin, 3000);
          return;
        }
        window.history.replaceState(null, '', window.location.pathname);
        setMessage('Please enter your new password below.');
      });
    } else {
      setError('Invalid or expired password reset link. Please request a new one.');
      setTimeout(goToLogin, 3000);
    }
  }, [goToLogin]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    try {
      // Session may come from PKCE (code exchange) or from hash (implicit) — either way we need a session
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        throw new Error('Your reset link has expired or was already used. Please request a new password reset from the login page.');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      console.log('✅ Password updated successfully');
      
      // After password reset, immediately sign out to clear any session
      // User must sign in again with their new password
      // This prevents AuthContext from trying to load user with an invalid/transitional session
      console.log('🔒 Signing out after password reset to clear session...');
      
      // Clear any local storage first
      try {
        localStorage.removeItem('sb-auth-token');
        sessionStorage.clear();
      } catch (e) {
        console.warn('Could not clear storage:', e);
      }
      
      // Sign out from Supabase
      try {
        await supabase.auth.signOut();
        console.log('✅ Signed out successfully');
      } catch (signOutError) {
        console.warn('⚠️  Error signing out (non-fatal):', signOutError);
      }
      
      setMessage('Password reset successfully! Redirecting to login...');
      
      // Clear the hash from URL immediately
      window.history.replaceState(null, '', window.location.pathname);
      
      // Redirect to login immediately (don't wait) to prevent AuthContext from trying to load user
      navigate('/login', {
        replace: true,
        state: {
          message: 'Password reset successfully. Please sign in with your new password.',
        },
      });

    } catch (err) {
      console.error('Password reset error:', err);
      if (err.message && err.message.includes('expired')) {
        setError('This password reset link has expired. Please request a new one from the login page.');
      } else {
        setError(err.message || 'An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <ZaggerLogo variant="full" darkMode={false} />
          <h1>Reset Password</h1>
          <p>Enter your new password</p>
        </div>

        <div className="login-form-container">
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {message && (
            <div className="alert alert-success">
              {message}
            </div>
          )}

          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your new password (min 8 characters)"
                required
                disabled={loading}
                autoComplete="new-password"
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                disabled={loading}
                autoComplete="new-password"
                minLength={8}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || !message}
            >
              {loading ? 'Resetting password...' : 'Reset Password'}
            </button>
          </form>

          <div className="login-actions">
            <button
              type="button"
              className="btn-link"
              onClick={goToLogin}
              disabled={loading}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
